import React, { useState, useEffect } from "react";
import {
  MultiSelectDropdownWithField,
  MultiSelectOption,
} from "./dropdowns/MultiSelectDropdownWithField";
import { TagItem, fetchTags } from "@/lib/stores/tagsStore";
import { useSimplifiedUserData } from "@/providers/SimplifiedUserDataProvider";

import { usePlatformTranslation } from '@/hooks/usePlatformTranslation';
interface TagManagerProps {
  tags: string[]; // Still accept string[] for backward compatibility
  onTagsChange: (tags: string[]) => void;
  blueprintTags?: string[];
  mode?: "create" | "edit" | "view";
  className?: string;
  label?: string;
  placeholder?: string;
  showLegend?: boolean;
  showTagSelector?: boolean;
  showSelectedOptions?: boolean; // New prop to control dropdown selected options display
}

export const TagManager: React.FC<TagManagerProps> = ({
  tags,
  onTagsChange,
  blueprintTags = [],
  mode = "create",
  className = "",
  label = "Tags",
  placeholder = "Add a tag...",
  showLegend = true,
  showTagSelector = true,
  showSelectedOptions = false, // Default to false since TagManager has its own display area
}) => {
  const { t } = usePlatformTranslation();
  const { selectedGame } = useSimplifiedUserData();
  const [availableTags, setAvailableTags] = useState<TagItem[]>([]);
  const [isLoadingTags, setIsLoadingTags] = useState(false);

  // Fetch available tags when component mounts
  useEffect(() => {
    if (selectedGame?.id && showTagSelector) {
      setIsLoadingTags(true);
      fetchTags(selectedGame.id)
        .then(setAvailableTags)
        .catch(console.error)
        .finally(() => setIsLoadingTags(false));
    }
  }, [selectedGame?.id, showTagSelector]);

  // Convert available tags to MultiSelectOption format
  const tagOptions: MultiSelectOption[] = availableTags.map((tag) => ({
    id: tag.id,
    label: tag.name,
    description: tag.group_name,
    disabled: blueprintTags.includes(tag.id), // Disable blueprint tags so they can't be unselected
  }));

  // Get currently selected tag IDs (for the dropdown) - include both blueprint tags and user-selected tags
  const selectedTagIds = [
    ...blueprintTags, // Always include blueprint tags
    ...tags
      .map((tagName) => availableTags.find((tag) => tag.name === tagName)?.id)
      .filter(Boolean)
      .filter(id => !blueprintTags.includes(id)) // Exclude duplicates that are already in blueprintTags
  ] as string[];

  // Get all tags that should be displayed (only user-selected tags, not blueprint tags)
  const allDisplayTags = availableTags.filter((tag) => 
    tags.includes(tag.name) // Only show tags that are in the user's tags array
  );

  const handleTagSelectionChange = (selectedValues: string | string[]) => {
    const selectedIds = Array.isArray(selectedValues)
      ? selectedValues
      : selectedValues
      ? [selectedValues]
      : [];

    // Always include blueprint tags
    const finalSelectedIds = [
      ...blueprintTags,
      ...selectedIds.filter(id => !blueprintTags.includes(id))
    ];

    // Convert selected tag IDs back to tag names
    const newTagNames = finalSelectedIds
      .map((tagId) => availableTags.find((tag) => tag.id === tagId)?.name)
      .filter(Boolean) as string[];

    onTagsChange(newTagNames);
  };

  const handleRemoveTag = (tagToRemove: string) => {
    // Don't allow removal of blueprint tags
    const tagToRemoveId = availableTags.find(tag => tag.name === tagToRemove)?.id;
    if (tagToRemoveId && blueprintTags.includes(tagToRemoveId)) {
      return;
    }
    onTagsChange(tags.filter((tag) => tag !== tagToRemove));
  };

  const isFromBlueprint = (tagId: string) => blueprintTags.includes(tagId);

  return (
    <div className={`space-y-2 ${className}`}>
      <label className="block text-sm font-medium text-gray-800">{label}</label>

      <div className="space-y-2">
        {/* Tag Selector - Only show in create/edit mode */}
        {mode !== "view" && showTagSelector && availableTags.length > 0 && (
          <MultiSelectDropdownWithField
            value={selectedTagIds}
            onChange={handleTagSelectionChange}
            options={tagOptions}
            placeholder={placeholder}
            multiple={true}
            isLoading={isLoadingTags}
            className="w-full"
            showSelectedOptions={showSelectedOptions}
          />
        )}

        {/* Manual Tag Input - Fallback when no tag selector or no available tags */}
        {mode !== "view" &&
          (!showTagSelector || availableTags.length === 0) && (
            <div className="flex gap-2">
              <input
                type="text"
                dir="auto"
                placeholder={t('platform.ui.tagmanager.type_tag_name_and_press_enter')}
                className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                onKeyDown={(e) => {
                  if (e.key === "Enter" && e.currentTarget.value.trim()) {
                    const newTag = e.currentTarget.value.trim();
                    if (!tags.includes(newTag)) {
                      onTagsChange([...tags, newTag]);
                    }
                    e.currentTarget.value = "";
                  }
                }}
              />
            </div>
          )}

        {/* Tags Display */}
        <div className="p-3 bg-white border border-gray-200 rounded-sm min-h-[40px]">
          {allDisplayTags.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {allDisplayTags.map((tag) => {
                const isBlueprintTag = isFromBlueprint(tag.id);
                return (
                  <span
                    key={tag.id}
                    className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      isBlueprintTag
                        ? "bg-blue-100 text-blue-800 border border-blue-200"
                        : "bg-purple-100 text-purple-800 border border-purple-200"
                    }`}
                    title={
                      isBlueprintTag
                        ? `Tag from blueprint${
                            tag.description
                              ? ` - Group: ${tag.description}`
                              : ""
                          }`
                        : tag.description
                        ? `Custom tag - Group: ${tag.description}`
                        : "Custom tag"
                    }
                  >
                    {tag.name}
                    {mode !== "view" && !isBlueprintTag && (
                      <button
                        type="button"
                        onClick={() => handleRemoveTag(tag.name)}
                        className="ml-1 hover:text-opacity-80 focus:outline-none"
                      >
                        ×
                      </button>
                    )}
                  </span>
                );
              })}
            </div>
          ) : (
            <span className="text-gray-500 text-sm">
              {blueprintTags.length > 0 
                ? "No custom tags selected yet" 
                : "No tags available"}
            </span>
          )}
        </div>
      </div>

      {/* Help Text */}
      <p className="text-xs text-gray-600">
        {mode === "view"
          ? "Tags are displayed below."
          : blueprintTags.length > 0
          ? "Blueprint tags are automatically included and cannot be removed. Custom tags you select will appear below."
          : showTagSelector && availableTags.length > 0
          ? "Select from available tags or add custom ones."
          : "Add tags to organize and categorize this item."}
      </p>

      {/* Tag Legend */}
      {showLegend && (allDisplayTags.length > 0 || blueprintTags.length > 0) && (
        <div className="flex items-center gap-4 text-xs text-gray-600">
          {blueprintTags.length > 0 && (
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 bg-blue-100 border border-blue-200 rounded-full"></span>
              <span>{t('platform.ui.tagmanager.from_blueprint_cannot_be_removed')}</span>
            </div>
          )}
          {allDisplayTags.length > 0 && (
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 bg-purple-100 border border-purple-200 rounded-full"></span>
              <span>{t('platform.ui.tagmanager.custom_tags')}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
